import { useState } from 'react';
import {
  Users,
  Package,
  CalendarCheck,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Building2,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subMonths, startOfDay, endOfDay } from 'date-fns';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useAuthStore } from '@/stores/auth-store';
import { DateRangePicker } from '@/components/shared/date-range-picker';
import {
  RevenueChart,
  BookingStatusChart,
  PaymentAlertsWidget,
  DocumentAlertsWidget,
  DepartureUtilizationChart,
} from '@/components/travel/dashboard';

const statusColors: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED: 'bg-blue-50 text-blue-700 border-blue-200',
  PARTIALLY_PAID: 'bg-orange-50 text-orange-700 border-orange-200',
  FULLY_PAID: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  TICKETED: 'bg-purple-50 text-purple-700 border-purple-200',
  IN_PROGRESS: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  COMPLETED: 'bg-slate-50 text-slate-700 border-slate-200',
  CANCELLED: 'bg-red-50 text-red-700 border-red-200',
  REFUNDED: 'bg-pink-50 text-pink-700 border-pink-200',
};

const statusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  CONFIRMED: 'status.confirmed',
  PARTIALLY_PAID: 'status.partiallyPaid',
  FULLY_PAID: 'status.fullyPaid',
  TICKETED: 'status.ticketed',
  IN_PROGRESS: 'status.inProgress',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

export function DashboardPage() {
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation('dashboard');
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfDay(subMonths(new Date(), 1)),
    to: endOfDay(new Date()),
  });
  const { data, isLoading, error } = trpc.dashboard.stats.useQuery({
    dateFrom: dateRange.from.toISOString(),
    dateTo: dateRange.to.toISOString(),
  });

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greetingMorning');
    if (hour < 18) return t('greetingAfternoon');
    return t('greetingEvening');
  };

  // Get locale for date formatting
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-destructive/20 bg-destructive/5 p-6">
        <p className="text-sm font-semibold text-destructive">{t('errorTitle')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  const stats = data?.stats;
  const recentBookings = data?.recentBookings ?? [];
  const upcomingDepartures = data?.upcomingDepartures ?? [];

  const statCards = [
    {
      key: 'totalRevenue',
      value: stats?.revenue.total ?? 0,
      change: stats?.revenue.change ?? 0,
      icon: DollarSign,
      format: 'currency' as const,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      key: 'activeBookings',
      value: stats?.bookings.active ?? 0,
      change: stats?.bookings.change ?? 0,
      icon: CalendarCheck,
      format: 'number' as const,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      key: 'totalCustomers',
      value: stats?.customers.total ?? 0,
      change: stats?.customers.change ?? 0,
      icon: Users,
      format: 'number' as const,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      key: 'publishedPackages',
      value: stats?.packages.published ?? 0,
      change: 0,
      icon: Package,
      format: 'number' as const,
      hideChange: true,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Compact Header */}
      <div className="border-b border-border pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {new Date().toLocaleDateString(dateLocale, { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h1 className="mt-1 text-xl font-semibold">
              {getGreeting()}, {user?.firstName || 'there'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const isIncrease = stat.change >= 0;
          return (
            <div
              key={stat.key}
              className="border border-border bg-card p-4"
            >
              <div className="flex items-center justify-between">
                <div className={cn('p-2', stat.iconBg)}>
                  <stat.icon className={cn('h-4 w-4', stat.iconColor)} />
                </div>
                {!stat.hideChange && stat.change !== 0 && (
                  <div
                    className={cn(
                      'flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                      isIncrease ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                    )}
                  >
                    {isIncrease ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {Math.abs(stat.change)}%
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-2xl font-semibold tabular-nums">
                  {stat.format === 'currency'
                    ? formatCurrency(stat.value)
                    : formatNumber(stat.value)}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t(stat.key)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-3">
        {[
          {
            icon: CalendarCheck,
            value: stats?.bookings.thisMonth ?? 0,
            labelKey: 'bookingsThisMonth',
            color: 'text-blue-600',
            bg: 'bg-blue-100',
          },
          {
            icon: Users,
            value: stats?.customers.thisMonth ?? 0,
            labelKey: 'newCustomersThisMonth',
            color: 'text-emerald-600',
            bg: 'bg-emerald-100',
          },
          {
            icon: Building2,
            value: stats?.partners.active ?? 0,
            labelKey: 'activePartners',
            color: 'text-violet-600',
            bg: 'bg-violet-100',
          },
        ].map((item) => (
          <div
            key={item.labelKey}
            className="border border-border bg-card p-4 flex items-center gap-3"
          >
            <div className={cn('p-2', item.bg)}>
              <item.icon className={cn('h-5 w-5', item.color)} />
            </div>
            <div>
              <p className="text-xl font-semibold tabular-nums">{item.value}</p>
              <p className="text-xs text-muted-foreground">{t(item.labelKey)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RevenueChart />
        </div>
        <BookingStatusChart />
      </div>

      {/* Alerts & Utilization Section */}
      <div className="grid gap-4 lg:grid-cols-3">
        <PaymentAlertsWidget />
        <DocumentAlertsWidget />
        <DepartureUtilizationChart />
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Recent Bookings */}
        <div className="lg:col-span-2 border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold">{t('recentBookings')}</h2>
              <p className="text-xs text-muted-foreground">{t('latestReservations')}</p>
            </div>
            <Link
              to="/travel/bookings"
              className="group flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium bg-muted hover:bg-muted/80 transition-colors"
            >
              {t('viewAll')}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          {recentBookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="bg-muted p-4">
                <CalendarCheck className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="mt-3 text-sm font-medium">{t('noBookingsYet')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('startByCreating')}</p>
              <Link
                to="/travel/bookings"
                className="mt-3 h-8 px-3 inline-flex items-center text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('createBooking')}
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentBookings.map((booking) => (
                <Link
                  key={booking.id}
                  to={`/travel/bookings/${booking.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium">{booking.reference}</span>
                      <span
                        className={cn(
                          'border px-2 py-0.5 text-[10px] font-medium',
                          statusColors[booking.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                        )}
                      >
                        {t(statusLabelKeys[booking.status] || 'status.pending') || booking.status}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground truncate">
                      {booking.customer} • {booking.package}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-semibold tabular-nums">{formatCurrency(booking.amount)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(booking.date).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Departures */}
        <div className="border border-border bg-card overflow-hidden">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">{t('upcomingDepartures')}</h2>
            <p className="text-xs text-muted-foreground">{t('toursStartingSoon')}</p>
          </div>
          {upcomingDepartures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <div className="bg-muted p-4">
                <Package className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="mt-3 text-sm font-medium">{t('noDeparturesScheduled')}</p>
              <p className="mt-1 text-xs text-muted-foreground text-center">{t('createPackagesWithDates')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {upcomingDepartures.map((departure) => (
                <div
                  key={departure.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight truncate">{departure.packageName}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {new Date(departure.departureDate).toLocaleDateString(dateLocale, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    {departure.isGuaranteed && (
                      <span className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                        {t('guaranteed')}
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-medium tabular-nums">
                        {departure.booked} / {departure.capacity}
                      </span>
                      <span className="text-muted-foreground">
                        {Math.round((departure.booked / departure.capacity) * 100)}% {t('filled')}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${(departure.booked / departure.capacity) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
